import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { Game } from '../types';
import { inventoryService } from '../services/api';

interface UnitCountersDialogProps {
  open: boolean;
  onClose: () => void;
  item: Game | null;
  onSave: (item: Game) => void;
}

const UnitCountersDialog: React.FC<UnitCountersDialogProps> = ({
  open,
  onClose,
  item,
  onSave,
}) => {
  const [counters, setCounters] = useState({
    excellent_count: 0,
    good_count: 0,
    fair_count: 0,
    poor_count: 0,
    broken_count: 0,
    available_count: 0,
    in_use_count: 0,
    maintenance_count: 0,
    retired_count: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (item) {
      setCounters({
        excellent_count: item.excellent_count || 0,
        good_count: item.good_count || 0,
        fair_count: item.fair_count || 0,
        poor_count: item.poor_count || 0,
        broken_count: item.broken_count || 0,
        available_count: item.available_count || 0,
        in_use_count: item.in_use_count || 0,
        maintenance_count: item.maintenance_count || 0,
        retired_count: item.retired_count || 0,
      });
      setError(null);
    }
  }, [item]);

  const handleCounterChange = (field: keyof typeof counters, value: number) => {
    setCounters(prev => ({
      ...prev,
      [field]: Math.max(0, value),
    }));
  };

  const validateCounters = () => {
    const healthTotal = counters.excellent_count + counters.good_count + 
                       counters.fair_count + counters.poor_count + counters.broken_count;
    const availabilityTotal = counters.available_count + counters.in_use_count + 
                             counters.maintenance_count + counters.retired_count;
    
    if (item && (healthTotal !== item.quantity || availabilityTotal !== item.quantity)) {
      return `Los contadores deben sumar exactamente ${item.quantity} unidades`;
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validateCounters();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!item) return;

    setLoading(true);
    setError(null);

    try {
      const updatedItem = await inventoryService.updateUnitCounters(item.id, counters);
      onSave(updatedItem);
      onClose();
    } catch (err) {
      setError('Error al actualizar los contadores');
      console.error('Error updating unit counters:', err);
    } finally {
      setLoading(false);
    }
  };

  const healthTotal = counters.excellent_count + counters.good_count + 
                     counters.fair_count + counters.poor_count + counters.broken_count;
  const availabilityTotal = counters.available_count + counters.in_use_count + 
                           counters.maintenance_count + counters.retired_count;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Gestionar Contadores de Unidades - {item?.name}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Estado de Salud de las Unidades
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={2.4}>
              <TextField
                label="Excelente"
                type="number"
                value={counters.excellent_count}
                onChange={(e) => handleCounterChange('excellent_count', parseInt(e.target.value) || 0)}
                fullWidth
                size="small"
                color="success"
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <TextField
                label="Bueno"
                type="number"
                value={counters.good_count}
                onChange={(e) => handleCounterChange('good_count', parseInt(e.target.value) || 0)}
                fullWidth
                size="small"
                color="primary"
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <TextField
                label="Regular"
                type="number"
                value={counters.fair_count}
                onChange={(e) => handleCounterChange('fair_count', parseInt(e.target.value) || 0)}
                fullWidth
                size="small"
                color="warning"
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <TextField
                label="Malo"
                type="number"
                value={counters.poor_count}
                onChange={(e) => handleCounterChange('poor_count', parseInt(e.target.value) || 0)}
                fullWidth
                size="small"
                color="warning"
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <TextField
                label="Roto"
                type="number"
                value={counters.broken_count}
                onChange={(e) => handleCounterChange('broken_count', parseInt(e.target.value) || 0)}
                fullWidth
                size="small"
                color="error"
              />
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Total salud: {healthTotal} / {item?.quantity}
          </Typography>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom>
            Estado de Disponibilidad de las Unidades
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={2.4}>
              <TextField
                label="Disponible"
                type="number"
                value={counters.available_count}
                onChange={(e) => handleCounterChange('available_count', parseInt(e.target.value) || 0)}
                fullWidth
                size="small"
                color="success"
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <TextField
                label="En Uso"
                type="number"
                value={counters.in_use_count}
                onChange={(e) => handleCounterChange('in_use_count', parseInt(e.target.value) || 0)}
                fullWidth
                size="small"
                color="info"
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <TextField
                label="Mantenimiento"
                type="number"
                value={counters.maintenance_count}
                onChange={(e) => handleCounterChange('maintenance_count', parseInt(e.target.value) || 0)}
                fullWidth
                size="small"
                color="warning"
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <TextField
                label="Retirado"
                type="number"
                value={counters.retired_count}
                onChange={(e) => handleCounterChange('retired_count', parseInt(e.target.value) || 0)}
                fullWidth
                size="small"
                color="error"
              />
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Total disponibilidad: {availabilityTotal} / {item?.quantity}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading}
        >
          {loading ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnitCountersDialog;
